import { computed, inject } from 'vue'
import { storeToRefs } from 'pinia'
import type { keyMapItem, GcomeSocdStrategy } from '@rapoo/device-sdk'
import { KeyTravelFamily } from '@rapoo/device-sdk'
import { toast } from '@rapoo/element'
import { useDeviceStore, useLocaleStore } from '@/stores'
import { KeyLayerSymbol, KeyPlatformSymbol, type KeySetting } from '../../../types'
import { parseNumber } from '../../../utils/keyIdentity'
import {
  buildAdvancedRestoreMatrixPayload,
  buildAdvancedStandardWritePlan,
  buildAdvancedWritePlan,
  createEmptyDksSlot,
  createEmptyMtSlot,
  createEmptyTglSlot,
  isAdvancedKeyType,
  isDksSlotEmpty,
  isMtSlotEmpty,
  isTglSlotEmpty,
  type AdvancedLists,
  type AdvancedSlotKind,
  type AdvancedWritePlan,
} from '../../../utils/advancedKeyMapSettings'
import { useKeySDK } from './useKeySDK'

/**
 * 单个高级改键信息区的占用情况。
 * total 取 SDK 列表长度——信息区 slot 数即该类型可创建的绑定数上限，
 * 其中 MT / RS / SOCD 共用同一个 MT 信息区（RS、SOCD 每对占 2 个 slot）。
 */
export type AdvancedSlotUsage = Record<AdvancedSlotKind, { used: number; total: number }>

export function useAdvancedKeyCommit() {
  const { t } = useLocaleStore()
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const currentLayer = inject(KeyLayerSymbol)
  const currentPlatform = inject(KeyPlatformSymbol)
  const isMacFnReadOnly = computed(
    () => currentPlatform?.value === 'mac' && currentLayer?.value === 'fn',
  )
  const {
    getKeyFeature,
    getDksList,
    getMtList,
    getTglList,
    setDksByIndex,
    setMtByIndex,
    setTglByIndex,
  } = useKeySDK()

  /**
   * 读取 DKS / MT / TGL 三个高级改键信息区列表。
   *
   * 每次下发前都要重新读取，因为 slot 是「按板载」分配、被 4 个语义层共用的：
   * 当前层的 keySettings 只看得到本层占用，其余层（FN / mac 层）占用的 slot 在这里
   * 不可见，信息区本身才是空闲 slot 的唯一可信来源。写入计划只用到这三个列表，
   * 触发行程不在此读取——RS/SOCD 的行程由 applyRsKeyTravel 单独读-改-写。
   */
  const loadAdvancedLists = async (): Promise<AdvancedLists> => {
    const [dksList, mtList, tglList] = await Promise.all([getDksList(), getMtList(), getTglList()])
    return {
      dksList: dksList ?? [],
      mtList: mtList ?? [],
      tglList: tglList ?? [],
    }
  }

  /**
   * 统计各高级改键信息区已占用 / 可用 slot 数，用于新建前的数量上限校验。
   * 读取失败时 total 为 0，由调用方视为「未知」不做限制。
   */
  const loadAdvancedSlotUsage = async (): Promise<AdvancedSlotUsage> => {
    const { dksList, mtList, tglList } = await loadAdvancedLists()
    return {
      dks: { used: dksList.filter((slot) => !isDksSlotEmpty(slot)).length, total: dksList.length },
      mt: { used: mtList.filter((slot) => !isMtSlotEmpty(slot)).length, total: mtList.length },
      tgl: { used: tglList.filter((slot) => !isTglSlotEmpty(slot)).length, total: tglList.length },
    }
  }

  const setLayerKeysMap = async (item: keyMapItem, isFn?: boolean) => {
    const feature = getKeyFeature()
    if (!feature) return
    await feature.setLayerKeysMap(item, isFn, currentPlatform?.value)
  }

  const applyAdvancedWritePlan = async (plan: AdvancedWritePlan, isFn?: boolean) => {
    if (isMacFnReadOnly.value) return

    for (const write of plan.listWrites) {
      if (write.kind === 'dks') await setDksByIndex(write.slot, write.index)
      else if (write.kind === 'mt') await setMtByIndex(write.slot, write.index)
      else await setTglByIndex(write.slot, write.index)
    }

    for (const item of plan.matrixItems) {
      await setLayerKeysMap(item, isFn)
    }

    for (const clear of plan.listClears) {
      if (clear.kind === 'dks') {
        await setDksByIndex(createEmptyDksSlot(clear.index), clear.index)
      } else if (clear.kind === 'mt') {
        await setMtByIndex(createEmptyMtSlot(clear.index), clear.index)
      } else {
        await setTglByIndex(createEmptyTglSlot(clear.index), clear.index)
      }
    }
  }

  const applyRsKeyTravel = async (setting: KeySetting) => {
    const keyTravel = currentDevice.value?.features.keyTravel
    if (!keyTravel) return

    const index1 = parseNumber(setting.raw?.index)
    const index2 = parseNumber(setting.raw?.peerKeyIndex ?? setting.raw?.relatedKeyIndex)
    if (index1 == null || index2 == null) return

    if (setting.keyType === 'SOCD') {
      const socd = setting.raw?.socd
      const combineRT = parseNumber(socd?.combineRT) === 1
      const triggerThreshold = parseNumber(socd?.triggerThreshold) ?? 0
      const subMode = parseNumber(socd?.subMode) ?? 1
      const priorityOnKey1 = subMode === 2 && parseNumber(socd?.selfPriority) === 1

      const socdStrategyForKey = (isKey1: boolean): GcomeSocdStrategy => {
        if (subMode === 3) return 0x30
        if (subMode === 2) {
          if (priorityOnKey1) return isKey1 ? 0x10 : 0x20
          return isKey1 ? 0x20 : 0x10
        }
        return 0x00
      }

      await keyTravel.setKeyTravel([
        {
          family: KeyTravelFamily.Gcome,
          index: index1,
          keyTravel: {
            triggerType: combineRT ? 2 : 0,
            travel: triggerThreshold,
            socdStrategy: socdStrategyForKey(true),
          },
        },
        {
          family: KeyTravelFamily.Gcome,
          index: index2,
          keyTravel: {
            triggerType: combineRT ? 2 : 0,
            travel: triggerThreshold,
            socdStrategy: socdStrategyForKey(false),
          },
        },
      ])
      return
    }

    const combineRT = parseNumber(setting.raw?.rs?.combineRT) === 1
    const triggerThreshold = parseNumber(setting.raw?.rs?.triggerThreshold) ?? 0

    await keyTravel.setKeyTravel(
      [index1, index2].map((index) => ({
        family: KeyTravelFamily.Gcome,
        index,
        keyTravel: {
          triggerType: combineRT ? 2 : 0,
          travel: triggerThreshold,
        },
      })),
    )
  }

  /**
   * 清掉 SOCD 绑定在两个键位行程参数上留下的优先级策略。
   * 矩阵条目恢复成普通键后这块策略不会自动复位，残留会影响该键的正常触发。
   */
  const clearSocdKeyTravel = async (setting: KeySetting) => {
    if (isMacFnReadOnly.value) return

    const keyTravel = currentDevice.value?.features.keyTravel
    if (!keyTravel) return

    const indexes = [
      parseNumber(setting.raw?.index),
      parseNumber(setting.raw?.relatedKeyIndex ?? setting.raw?.peerKeyIndex),
    ].filter((index): index is number => index != null)
    if (!indexes.length) return

    await keyTravel.setKeyTravel(
      indexes.map((index) => ({
        family: KeyTravelFamily.Gcome,
        index,
        keyTravel: { socdStrategy: 0 },
      })),
    )
  }

  const commitAdvancedKey = async ({
    setting,
    existingSetting,
    peerExistingSetting,
    isFn,
  }: {
    setting: KeySetting
    existingSetting?: KeySetting
    peerExistingSetting?: KeySetting
    isFn?: boolean
  }): Promise<boolean> => {
    if (isMacFnReadOnly.value) return false

    try {
      const lists = await loadAdvancedLists()
      const plan = buildAdvancedWritePlan({ setting, lists, existingSetting, peerExistingSetting })
      if (!plan) {
        toast.info(t('toast.keys.advancedSlotFull'))
        return false
      }
      await applyAdvancedWritePlan(plan, isFn)
      if (setting.keyType === 'RS' || setting.keyType === 'SOCD') {
        await applyRsKeyTravel(setting)
      }
      return true
    } catch (error) {
      console.error('Failed to commit advanced key', error)
      toast(t('toast.device.fail'))
      return false
    }
  }

  const restoreAdvancedKey = async ({
    setting,
    defaultKey,
    factoryKeywords,
    isFn,
    clearSlot = true,
  }: {
    setting?: KeySetting
    defaultKey: {
      keywords?: string
      type?: unknown
      code?: unknown
      confIndex?: unknown
      key?: unknown
    }
    factoryKeywords?: string
    isFn?: boolean
    clearSlot?: boolean
  }) => {
    if (isMacFnReadOnly.value) return

    const payload = buildAdvancedRestoreMatrixPayload({ setting, defaultKey, factoryKeywords })
    if (!payload) return

    if (clearSlot && isAdvancedKeyType(setting?.keyType)) {
      await applyAdvancedWritePlan(
        buildAdvancedStandardWritePlan({ matrixItem: payload, existingSetting: setting }),
        isFn,
      )
      return
    }

    await setLayerKeysMap(payload, isFn)
  }

  return {
    loadAdvancedLists,
    loadAdvancedSlotUsage,
    applyAdvancedWritePlan,
    clearSocdKeyTravel,
    commitAdvancedKey,
    restoreAdvancedKey,
  }
}
